import { SyntheticEvent } from 'react';

import { ImageLoaderProps } from 'next/image';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { convertCount } from '@lib/numbers/convertCount';
import { getImgProxyLoader } from '@lib/images/imgProxyLoader';
import { getSpeciesUrl } from '@domain/routes/urls';
import { EventMykissCardClicked } from '@generated/events';
import { ChevronRightIcon } from '../../Icons/ChevronRightIcon';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { Text } from '../../Text/Text';
import { LinkCard } from '../LinkCard/LinkCard';

interface IProps {
  testId?: string;
  name: string;
  numberCaught?: number;
  recentCaught?: number;
  speciesId: string;
  /* An identifier for where this carousel is being used, e.g. species-top-waters */
  clickEventSource: EventMykissCardClicked['properties']['source'];
  /* An ID of the resource type where this carousel is being used, e.g. a species ID if it's on a species page */
  clickEventSourceId?: EventMykissCardClicked['properties']['source_id'];
  /* The position of the card in a list (if applicable) */
  clickEventPosition?: EventMykissCardClicked['properties']['position'];
  className?: string;
  imageClassName?: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  isDisabled?: boolean;
  useCustomLoader?: boolean;
  onClick?: (event: SyntheticEvent) => void;
}

export const TopSpeciesLinkCard = ({
  testId,
  speciesId,
  name,
  clickEventSource,
  numberCaught,
  recentCaught,
  clickEventPosition,
  clickEventSourceId,
  className = '',
  isDisabled = false,
  imageClassName = '',
  imageUrl,
  imageWidth,
  imageHeight,
  useCustomLoader = true,
  onClick,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
    getImgProxyLoader(src, width, imageWidth ?? 0, imageHeight, { quality });

  return (
    <LinkCard
      onClick={onClick}
      to={getSpeciesUrl(speciesId, name)}
      withDefaults={false}
      disabled={isDisabled}
      data-testid={testId}
      eventProperties={{
        source: clickEventSource,
        target: 'species',
        target_id: speciesId,
        position: clickEventPosition,
        source_id: clickEventSourceId,
        page: currentPage,
      }}
      className={twMerge('border-b-twilight relative w-full border-b py-2.5', className)}
    >
      <div className="relative flex items-center gap-4">
        {imageUrl && imageWidth && imageHeight ? (
          <NextImageWrapper
            wrapperClassName="h-11 w-28"
            imageClassName={twMerge('size-full object-contain', imageClassName)}
            customPlaceholderIcon={
              <FishCatchesIcon className="size-full object-contain" size={93} />
            }
            customPlaceholderClassName="text-twilight p-0 bg-sea-foam sm:bg-lake-fog sm:text-twilight"
            alt={name}
            src={imageUrl}
            loader={useCustomLoader ? imgProxyLoader : undefined}
            fill
            sizes="112px"
          />
        ) : (
          <div className="bg-twilight text-lake-fog mb-3 flex h-11 w-28 items-center justify-center rounded-md md:rounded-lg">
            <FishCatchesIcon size={93} />
          </div>
        )}
        <div className="flex grow flex-col">
          <Text as="div" variant="sm" className="m-0 font-semibold">
            {name}
          </Text>
          {numberCaught && (
            <div className="flex items-center gap-1">
              <Text as="span" variant="xs" className="">
                {convertCount(numberCaught)} {numberCaught === 1 ? ' catch' : ' catches'}
              </Text>
              {recentCaught !== undefined && recentCaught > 0 && (
                <span className="text-new-flag bg-clownfish-shade rounded-md px-1.5 text-white">
                  {recentCaught} NEW
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-abyss mr-1.5">
          <ChevronRightIcon />
        </div>
      </div>
    </LinkCard>
  );
};
